import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { wz } from '../../components/wizard-tokens';
import { WizGradientButton, WizInput, WizSwitch } from '../../components/wizard-ui';

interface Props {
  /** Zählt hoch, wenn die Frage Fokus bekommen soll (Weiter ohne Ort). */
  focusTick: number;
  /** Hervorheben (Weiter wurde ohne Ort versucht). */
  invalid?: boolean;
  /** Antwort: Ort (leer bei „100 % remote“) und ob die Stelle voll remote ist. */
  onSubmit: (city: string, remote: boolean) => void;
}

/** Pflichtfrage im Prüfergebnis: „Wo ist die Stelle?“ – Ort oder „100 % remote“. */
const LocationQuestion: React.FC<Props> = ({ focusTick, invalid = false, onSubmit }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [city, setCity] = useState('');
  const [remote, setRemote] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const canSubmit = remote || city.trim().length > 0;

  useEffect(() => {
    if (focusTick > 0) {
      box.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      box.current?.querySelector('input')?.focus();
    }
  }, [focusTick]);

  const submit = () => {
    if (canSubmit) onSubmit(remote ? '' : city.trim(), remote);
  };

  return (
    <div
      ref={box}
      className={classes.box}
      style={invalid ? { borderColor: wz.redFg } : undefined}
      data-testid="wiz-location-question"
    >
      <div className={classes.title}>{t('wizLocationTitle')}</div>
      <div className={classes.hint}>{t('wizLocationHint')}</div>
      <div className={classes.row}>
        <WizInput
          value={city}
          onChange={setCity}
          disabled={remote}
          invalid={invalid && !canSubmit}
          placeholder={t('wizLocationPlaceholder')}
          aria-label={t('wizLocationTitle')}
          maxLength={120}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
        />
        <WizGradientButton onClick={submit} disabled={!canSubmit}>
          {t('wizLocationSubmit')}
        </WizGradientButton>
      </div>
      <WizSwitch on={remote} onToggle={() => setRemote(r => !r)} label={t('wizLocationRemote')} />
    </div>
  );
};

const useStyles = makeStyles()({
  box: {
    border: '1.5px solid #ED6C02',
    borderRadius: 10,
    padding: '12px 14px',
    margin: '8px 0 4px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  title: { fontSize: 14, fontWeight: 700, color: wz.text },
  hint: { fontSize: 13, color: wz.muted },
  row: { display: 'flex', gap: 10, alignItems: 'center' },
});

export default LocationQuestion;
